import React, { Suspense, useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, Float, Center, Environment } from '@react-three/drei';
import * as THREE from 'three';
import retroMonitorUrl from '../../assets/retro_monitor.glb';
import meVidUrl        from '../../assets/me_vid.mp4';

const SCREEN_NODE = 'Object_6';

// ── Monitor ───────────────────────────────────────────────────────────────────
function Monitor() {
  const { scene }  = useGLTF(retroMonitorUrl);
  const screenRef  = useRef(null);
  const lightRef   = useRef(null);
  const emCur      = useRef(0);
  const emTarget   = useRef(0);
  const screenOn   = useRef(false);

  // Glitch state
  const gRef       = useRef({ timer: 0, nextAt: 0.6, active: false, endAt: 0 });
  const canvasRef  = useRef(null);
  const cTexRef    = useRef(null);
  const videoRef   = useRef(null);
  const vTexRef    = useRef(null);

  useEffect(() => {
    // ── Trova il mesh dello schermo ──────────────────────────────────────────
    scene.traverse(child => {
      if (!child.isMesh || child.name !== SCREEN_NODE) return;
      screenRef.current = child;
      child.material = child.material.clone();
      child.material.emissive      = new THREE.Color(1, 1, 1);
      child.material.emissiveIntensity = 0;
      child.material.needsUpdate   = true;
    });

    // ── Video element ────────────────────────────────────────────────────────
    const video = document.createElement('video');
    video.src         = meVidUrl;
    video.muted       = true;
    video.loop        = true;
    video.playsInline = true;
    video.autoplay    = true;
    video.preload     = 'auto';
    // Appeso a <html> (fuori dal sottoalbero React) per garantire la decodifica
    // dei frame senza interferire con il reconciler di React
    video.style.cssText =
      'position:fixed;left:-9999px;top:-9999px;pointer-events:none;width:2px;height:2px;';
    document.documentElement.appendChild(video);
    video.play().catch(() => {});

    // ── THREE.VideoTexture — aggiornata automaticamente da Three.js ──────────
    const vTex = new THREE.VideoTexture(video);
    vTex.colorSpace = THREE.SRGBColorSpace;
    vTex.flipY      = true;   // corregge l'orientamento del video su mesh GLB
    vTex.minFilter  = THREE.LinearFilter;
    vTex.magFilter  = THREE.LinearFilter;
    vTex.wrapS = THREE.ClampToEdgeWrapping;
    vTex.wrapT = THREE.ClampToEdgeWrapping;
    // offset sposta leggermente in alto senza zoom
    vTex.repeat.set(1, 1);
    vTex.offset.set(0, -0.12);

    // ── Canvas texture per gli effetti glitch ───────────────────────────────
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 512;
    const cTex = new THREE.CanvasTexture(canvas);
    cTex.colorSpace = THREE.SRGBColorSpace;
    cTex.flipY      = true;   // coerente con vTex
    cTex.minFilter  = THREE.LinearFilter;
    cTex.magFilter  = THREE.LinearFilter;

    videoRef.current  = video;
    vTexRef.current   = vTex;
    canvasRef.current = canvas;
    cTexRef.current   = cTex;

    // Applica la VideoTexture allo schermo appena il mesh è pronto
    const applyTex = () => {
      const mesh = screenRef.current;
      if (!mesh) return;
      const mat       = mesh.material;
      mat.map         = vTex;
      mat.emissiveMap = vTex;
      mat.needsUpdate = true;
    };

    if (screenRef.current) {
      applyTex();
    } else {
      // Riprova dopo che l'useEffect della traversal avrà settato screenRef
      const id = setTimeout(applyTex, 50);
      return () => clearTimeout(id);
    }

    return () => {
      video.pause();
      try { video.remove(); } catch (_) {}
      vTex.dispose();
      cTex.dispose();
    };
  }, [scene]);

  useFrame((_, delta) => {
    const video  = videoRef.current;
    const screen = screenRef.current;
    if (!screen) return;

    // ── Attiva lo schermo al primo scroll ────────────────────────────────────
    if (!screenOn.current && window.scrollY > 0) {
      screenOn.current = true;
      emTarget.current = 0.65;
    }

    // ── Lerp emissiveIntensity ───────────────────────────────────────────────
    emCur.current = THREE.MathUtils.lerp(
      emCur.current,
      emTarget.current,
      1 - Math.pow(0.015, delta),
    );
    screen.material.emissiveIntensity = emCur.current;
    if (lightRef.current) {
      lightRef.current.intensity = emCur.current * 2.5;
    }

    // ── Glitch (solo quando il video è pronto) ───────────────────────────────
    if (!video || video.readyState < 2 || video.videoWidth === 0) return;

    const g = gRef.current;
    g.timer += delta;
    if (!g.active && g.timer >= g.nextAt) {
      g.active = true;
      g.endAt  = g.timer + 0.04 + Math.random() * 0.12;
      g.nextAt = g.timer + 0.35 + Math.random() * 1.8;
    }
    if (g.active && g.timer >= g.endAt) g.active = false;

    if (g.active) {
      // Passa alla canvas texture durante il glitch
      if (screen.material.map !== cTexRef.current) {
        screen.material.map         = cTexRef.current;
        screen.material.emissiveMap = cTexRef.current;
        screen.material.needsUpdate = true;
      }

      const canvas = canvasRef.current;
      const cTex   = cTexRef.current;
      const vw     = video.videoWidth;
      const vh     = video.videoHeight;

      if (canvas.width !== vw) { canvas.width = vw; canvas.height = vh; }

      const ctx = canvas.getContext('2d');
      const { width: w, height: h } = canvas;

      // Shift in alto: sorgente shiftata del 12% coerente con vTex.offset(0, -0.12)
      const srcOff = Math.floor(video.videoHeight * 0.12);

      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight - srcOff, 0, 0, w, h);

      // 1. Slice displacement
      const slices = 3 + Math.floor(Math.random() * 8);
      for (let i = 0; i < slices; i++) {
        const sy = Math.floor(Math.random() * h);
        const sh = 1 + Math.floor(Math.random() * 12);
        const dx = Math.round((Math.random() - 0.5) * 28);
        ctx.save();
        ctx.translate(dx, 0);
        ctx.drawImage(canvas, 0, sy, w, sh, 0, sy, w, sh);
        ctx.restore();
      }
      // 2. Ghost shift
      ctx.globalCompositeOperation = 'screen';
      ctx.globalAlpha = 0.22;
      const s = 4 + Math.random() * 7;
      ctx.drawImage(canvas, s, 0);
      ctx.drawImage(canvas, -s, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      // 3. Flash strip
      if (Math.random() > 0.4) {
        ctx.fillStyle = `rgba(255,255,255,${0.06 + Math.random() * 0.16})`;
        ctx.fillRect(0, Math.random() * h, w, 1 + Math.random() * 6);
      }
      // 4. Noise block
      if (Math.random() > 0.6) {
        ctx.fillStyle = Math.random() > 0.5
          ? `rgba(0,220,255,${0.2 + Math.random() * 0.3})`
          : `rgba(255,20,90,${0.18 + Math.random() * 0.22})`;
        ctx.fillRect(
          Math.random() * w * 0.85, Math.random() * h,
          15 + Math.random() * 90, 1 + Math.random() * 7,
        );
      }
      // 5. Inversion blink
      if (Math.random() > 0.75) {
        ctx.globalCompositeOperation = 'difference';
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(0, Math.floor(Math.random() * h), w, 2 + Math.floor(Math.random() * 6));
        ctx.globalCompositeOperation = 'source-over';
      }

      cTex.needsUpdate = true;
    } else {
      // Torna alla VideoTexture tra un glitch e l'altro
      if (screen.material.map !== vTexRef.current) {
        screen.material.map         = vTexRef.current;
        screen.material.emissiveMap = vTexRef.current;
        screen.material.needsUpdate = true;
      }
    }
  });

  return (
    <group>
      <primitive object={scene} />
      <pointLight
        ref={lightRef}
        position={[0, 0.15, 0.5]}
        color="#d8eeff"
        intensity={0}
        distance={2.5}
        decay={2}
      />
    </group>
  );
}

// ── Rotation animata via scroll progress ─────────────────────────────────────
// Interpola la Y rotation da -2 (stato iniziale) a -1.56 (monitor centrato)
// quando progressRef.current passa da 0 a 1.
function RotatingGroup({ progressRef, children }) {
  const groupRef = useRef(null);

  useFrame(() => {
    if (!groupRef.current) return;
    const p    = progressRef?.current ?? 0;
    const yRot = THREE.MathUtils.lerp(-2, -1.56, p);
    groupRef.current.rotation.y = yRot;
  });

  return (
    <group ref={groupRef} rotation={[0.08, -2, 0]}>
      {children}
    </group>
  );
}

// ── Canvas principale ─────────────────────────────────────────────────────────
export default function HeroMonitorScene({ progressRef }) {
  return (
    <Canvas
      gl={{ alpha: true, antialias: true }}
      camera={{ position: [0, 0, 5], fov: 42 }}
      style={{ background: 'transparent' }}
    >
      <Suspense fallback={null}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[4, 4, 4]}   intensity={1.2} />
        <directionalLight position={[-3, 1, -2]}  intensity={0.35} color="#FE5B00" />
        <directionalLight position={[0, -3, 2]}   intensity={0.2}  color="#00e5cc" />

        <Environment preset="city" />

        <Float speed={1.4} rotationIntensity={0.08} floatIntensity={0.25}>
          <Center>
            <RotatingGroup progressRef={progressRef}>
              <Monitor />
            </RotatingGroup>
          </Center>
        </Float>
      </Suspense>
    </Canvas>
  );
}

useGLTF.preload(retroMonitorUrl);
